import { Anchor, Body1, Flex } from '@phx/design-system';
import { useDisclosure } from '@phx/design-system/hooks';
import { useTranslation } from 'react-i18next';
import {
    createSearchParams,
    useNavigate,
    useSearchParams,
} from 'react-router-dom';

import {
    BrandNameCode,
    type MedicationEquivalent,
} from '../../graphql/generated/graphql';
import { getAbsoluteRoute } from '../../util/get-absolute-route';
import { ConditionalComponent } from '../utility/ConditionalComponent';

import { DrugFamilyEquivalentLinks } from './DrugFamilyEquivalentLinks';
import { DrugFamilyEquivalentsModal } from './DrugFamilyEquivalentsModal';

type DrugFamilyEquivalentsProps = {
    drugBrandNameCode: BrandNameCode;
    drugName: string;
    equivalents: MedicationEquivalent[] | null;
};

export const DrugFamilyEquivalents = ({
    drugBrandNameCode,
    drugName,
    equivalents,
}: DrugFamilyEquivalentsProps) => {
    const { t } = useTranslation();
    const [opened, { toggle }] = useDisclosure(false);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const handleClick = async (id: string, name: string) => {
        const updatedSearchParams = createSearchParams(searchParams);
        updatedSearchParams.set('medicationId', id);

        navigate({
            pathname: getAbsoluteRoute('medication.familyId.root', {
                familyId: name,
            }),
            search: updatedSearchParams.toString(),
        });
    };

    if (!equivalents?.length) {
        return null;
    }

    const displayedItems = equivalents.slice(0, 2);
    const extraCount = Math.max((equivalents.length ?? 0) - 2, 0);

    return (
        <Flex gap="xxxs" wrap="wrap">
            <Body1>
                {t(drugBrandNameCode, {
                    ns: 'code-locales/brand-name-code-mapping',
                    count: 1,
                })}
            </Body1>

            <ConditionalComponent condition={!!displayedItems}>
                <DrugFamilyEquivalentLinks
                    displayedItems={displayedItems}
                    handleClick={handleClick}
                />
            </ConditionalComponent>

            <ConditionalComponent condition={!!extraCount}>
                <Anchor onClick={toggle}>+{extraCount}</Anchor>
            </ConditionalComponent>

            <DrugFamilyEquivalentsModal
                drugName={drugName}
                equivalents={equivalents}
                opened={opened}
                handleClick={handleClick}
                toggle={toggle}
            />
        </Flex>
    );
};
